// src/services/chunkedUpload.jsx
import axios from 'axios';
import { API_BASES } from '../config';

const MANIFEST_KEY = 'aecf.upload.manifest.v1';
const DEFAULT_CHUNK = 8 * 1024 * 1024;

const UPLOADS_API = axios.create({ baseURL: `${API_BASES.files}/uploads` });

UPLOADS_API.interceptors.request.use(config => {
  const globalAuth = axios.defaults.headers.common['Authorization'];
  if (globalAuth) {
    config.headers.Authorization = globalAuth;
  } else {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Manifiesto persistido: permite reanudar tras recargar la página.
function readManifest() {
  try {
    const raw = localStorage.getItem(MANIFEST_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeManifest(manifest) {
  try {
    localStorage.setItem(MANIFEST_KEY, JSON.stringify(manifest));
  } catch {
    // sin almacenamiento se pierde la reanudación, no la subida
  }
}

export function clearPendingUpload() {
  try {
    localStorage.removeItem(MANIFEST_KEY);
  } catch {
    // noop
  }
}

function newGroupKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function manifestFor() {
  const existing = readManifest();
  if (existing && existing.groupKey && existing.files) return existing;
  const fresh = { groupKey: newGroupKey(), files: {} };
  writeManifest(fresh);
  return fresh;
}

const fileKey = file => `${file.name}|${file.size}|${file.lastModified}`;
const kindOf = file => ((file.type || '').startsWith('image/') ? 'PHOTO' : 'AUT');

async function initUpload(file, groupKey, productId) {
  const { data } = await UPLOADS_API.post('/init', {
    groupKey,
    name: file.name,
    size: file.size,
    contentType: file.type || 'application/octet-stream',
    kind: kindOf(file),
    productId: productId ?? null,
  });
  return data;
}

async function fetchStatus(uploadId) {
  try {
    const { data } = await UPLOADS_API.get(`/${uploadId}`);
    return data;
  } catch (err) {
    if (err.response && err.response.status === 404) return null;
    throw err;
  }
}

function putChunk(uploadId, start, total, chunk) {
  const end = start + chunk.size - 1;
  return UPLOADS_API.put(`/${uploadId}/chunk`, chunk, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'Content-Range': `bytes ${start}-${end}/${total}`,
    },
    validateStatus: status => (status >= 200 && status < 300) || status === 409 || status === 410,
  });
}

async function uploadOne(file, manifest, onProgress, productId) {
  const key = fileKey(file);
  let session = manifest.files[key] || null;
  let uploadId = session ? session.uploadId : null;
  let chunkSize = (session && session.chunkSize) || DEFAULT_CHUNK;
  let offset = 0;

  if (uploadId != null) {
    const status = await fetchStatus(uploadId);
    if (status) {
      offset = status.offset || 0;
      if (offset >= file.size) {
        onProgress(1);
        return;
      }
    } else {
      uploadId = null;
    }
  }

  while (offset < file.size) {
    if (uploadId == null) {
      const init = await initUpload(file, manifest.groupKey, productId);
      uploadId = init.uploadId;
      chunkSize = init.chunkSize || chunkSize;
      offset = init.offset || 0;
      if (init.groupKey && init.groupKey !== manifest.groupKey) {
        // El servidor reutilizó el grupo ya asociado al producto.
        manifest.groupKey = init.groupKey;
      }
      manifest.files[key] = { uploadId, chunkSize, size: file.size };
      writeManifest(manifest);
      if (offset >= file.size) break;
    }

    const end = Math.min(offset + chunkSize, file.size);
    const res = await putChunk(uploadId, offset, file.size, file.slice(offset, end));

    if (res.status === 410) {
      // Sesión de Drive caducada: se descarta y se reinicia.
      uploadId = null;
      offset = 0;
      delete manifest.files[key];
      writeManifest(manifest);
      continue;
    }
    if (res.status === 409) {
      offset = (res.data && res.data.expectedOffset) || 0;
      continue;
    }

    offset = res.data && typeof res.data.received === 'number' ? res.data.received : end;
    if (res.status === 201 || (res.data && res.data.complete)) offset = file.size;
    onProgress(Math.min(1, offset / file.size));
  }

  onProgress(1);
}

/**
 * Sube los archivos a file-service por chunks y devuelve el groupKey del grupo.
 * Con productId reutiliza la carpeta de Drive que ya tiene el producto.
 */
export async function uploadProductFiles(files, options = {}) {
  const list = (files || []).filter(f => f && f.size > 0);
  if (list.length === 0) return { groupKey: null };

  const { onProgress, productId } = options;
  const manifest = manifestFor();

  for (let i = 0; i < list.length; i++) {
    await uploadOne(list[i], manifest, fraction => {
      if (onProgress) onProgress(Math.round(((i + fraction) / list.length) * 100));
    }, productId);
  }

  return { groupKey: manifest.groupKey };
}
